import React, { useCallback, useEffect } from "react";
import { Formik } from "formik";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import { InfoIcon } from "../../../assets";

import {
  updateDriver,
  getDriverDetails,
  changeDriverStatus,
} from "../../../store/drivers";
import {
  fetchTOAdminPreferences,
  fetchTransportOrganization,
} from "../../../store/transport_organizations";
import { BackIcon, PreferencesIcon } from "../../../assets";
import { createDriverSchema, renderRecordValue } from "../../../utils";
import {
  CreateDriver,
  Button,
  Tooltip,
  useDisableUserModal,
} from "../../../components/";
import { vehicleOptionsDictionary } from "../../../dictionaries";
import { USER_ROLES, USER_STATUSES } from "../../../constants/constants";
import { LodashHelper } from "../../../utils";

import styles from "./index.module.css";
import { Navigation } from "../../../utils/navigation";

const UpdateDriver = ({
  driverDetails,
  rideOptions,
  vehicleTypes,
  history,
  role,
  fetchTOAdminPreferences,
  fetchTransportOrganization,
  updateDriver,
  getDriverDetails,
  changeDriverStatus,
  showAccountCheckbox,
}) => {
  const { id } = useParams();
  const navigate = Navigation();
  // MH Admin (SUPER_ADMIN) views this page read-only — see the effect below.
  const readOnly = role === USER_ROLES.SUPER_ADMIN;
  const handleConfirmDisableDriver = () => {
    changeDriverStatus({ id, status: { status: USER_STATUSES.DISABLED } });
    setDisableUserModalVisible(false);
  };
  const { DisableUserModal, setDisableUserModalVisible } = useDisableUserModal({
    handleConfirm: handleConfirmDisableDriver,
    userName:
      driverDetails?.firstName &&
      driverDetails?.lastName &&
      renderRecordValue(
        `${driverDetails?.firstName} ${driverDetails?.lastName}`
      ),
  });

  const toggleDisableUserModal = useCallback(
    (flag) => () => setDisableUserModalVisible(flag),
    [setDisableUserModalVisible]
  );

  useEffect(() => {
    getDriverDetails(id);
    return;
  }, [id, getDriverDetails]);

  // MH Admin views this page read-only and isn't a member of the driver's TC, so "my transport
  useEffect(() => {
    if (!readOnly) {
      fetchTOAdminPreferences();
      return;
    }

    if (driverDetails.organizationId) {
      fetchTransportOrganization(driverDetails.organizationId);
    }
  }, [readOnly, driverDetails.organizationId, fetchTOAdminPreferences, fetchTransportOrganization]);

  const handleBack = useCallback(() => navigate.back(), [navigate]);

  return (
    <div className={styles.pageContent}>
      <div className={styles.header}>
        <div className={styles.backBtn} onClick={handleBack}>
          <BackIcon width="26px" height="26px" fill="#E1F4D3" />
        </div>
        <h1 className={styles.pageTitle}>Driver Details </h1>
        {readOnly && <span className={styles.readOnlyBadge}>Read Only</span>}
      </div>
      <div>
        <h2>Information of the Driver</h2>
        <Formik
          enableReinitialize
          validationSchema={createDriverSchema}
          validateOnMount
          initialValues={driverDetails}
          onSubmit={(values) =>
            !readOnly && updateDriver({ id, values, Navigation: navigate })
          }
        >
          {({ values, ...rest }) => {
            return (
              <form className={styles.form} onSubmit={rest.handleSubmit}>
                <CreateDriver
                  values={values}
                  rideOptions={rideOptions || []}
                  vehicleTypes={vehicleTypes || []}
                  showPersonalInfo
                  showAccountCheckbox={showAccountCheckbox}
                  readOnly={readOnly}
                />
                {!readOnly && (
                <div className={styles.btnPanel}>
                  <div
                    className={classNames({
                      [styles.buttonWithTooltip]: true,
                      [styles.deactivateTooltip]: true,
                    })}
                  >
                    {driverDetails.status !== USER_STATUSES.DISABLED && (
                      <div className={styles.btnControl}>
                        <Button
                          myClassName={styles.navigationBtn}
                          onClick={toggleDisableUserModal(true)}
                          disabled={driverDetails.unableToDisable}
                        >
                          Deactivate Driver
                        </Button>
                      </div>
                    )}
                    {driverDetails.unableToDisable && (
                      <Tooltip
                        title={
                          "This driver has upcoming rides assigned. Prior to deactivating the driver, reassign the ride(s) to another driver in your organization. If you are unable to reassign the ride request(s), please Cancel the request."
                        }
                      >
                        <span className={styles.tooltipInfo}>
                          <InfoIcon width={30} height={30} className={styles.infoIcon} />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  <div
                    className={classNames({
                      [styles.buttonWithTooltip]: true,
                      [styles.inviteTooltip]: true,
                    })}
                  >
                    <div className={styles.btnControl}>
                      <Button
                        disabled={
                          !rest.isValid ||
                          LodashHelper.isEqual(values, driverDetails)
                        }
                        type="submit"
                      >
                        Save Changes
                      </Button>
                    </div>
                    <Tooltip
                      title={
                        <span className={styles.tooltipContent}>
                          The{" "}
                          <span className={styles.italicText}>
                            Vehicle Type(s){" "}
                          </span>{" "}
                          and
                          <span className={styles.italicText}>
                            {" "}
                            Additional driver/vehicle capabilities{" "}
                          </span>
                          fields are
                          <span
                            className={classNames({
                              [styles.boldText]: true,
                              [styles.required]: true,
                            })}
                          >
                            required
                          </span>
                          for the driver. Please fill in this information in the
                          Organization
                          <span className={styles.italicText}>
                            {" "}
                            Preferences{" "}
                          </span>
                          tab {<PreferencesIcon fill="#fff" />}
                        </span>
                      }
                    >
                      <span
                        className={classNames({
                          [styles.tooltipInfo]: true,
                          [styles.tooltipDisabled]:
                            !rideOptions?.length && !vehicleTypes?.length,
                        })}
                      >
                        <InfoIcon width={30} height={30} className={styles.infoIcon} />
                      </span>
                    </Tooltip>
                  </div>
                </div>
                )}
              </form>
            );
          }}
        </Formik>
      </div>
      {DisableUserModal}
    </div>
  );
};

const mapStateToProps = (state) => {
  const role = state.authorization.role || state?.authorization?.user?.role || "";
  const tcCatalog =
    role === USER_ROLES.SUPER_ADMIN
      ? state?.transportOrganizations?.currentTransportOrganization
      : state?.transportOrganizations?.TCPreferences;

  return {
    role,
    driverDetails: {
      email: state?.drivers?.driverDetails?.email,
      firstName: state?.drivers?.driverDetails?.firstName,
      lastName: state?.drivers?.driverDetails?.lastName,
      phoneNumber: state?.drivers?.driverDetails?.phoneNumber,
      jobTitle: state?.drivers?.driverDetails?.jobTitle,
      dob: state?.drivers?.driverDetails?.dob,
      hasAccount: state?.drivers?.driverDetails?.hasAccount,
      rideOptions: state?.drivers?.driverDetails?.rideOptions,
      vehicleTypes: state?.drivers?.driverDetails?.vehicleTypes,
      unableToDisable: state?.drivers?.driverDetails?.ridesCount > 0,
      canReceiveSms: state?.drivers?.driverDetails?.canReceiveSms,
      status: state?.drivers?.driverDetails?.status,
      driverRating: state?.drivers?.driverDetails?.driverRating,
      organizationId: state?.drivers?.driverDetails?.organizationId,
    },
    rideOptions: tcCatalog?.rideOptions,
    vehicleTypes: tcCatalog?.vehicleTypes?.map((tt, idx) => ({
      id: idx,
      title: vehicleOptionsDictionary[tt],
      value: tt,
    })),
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateDriver,
      getDriverDetails,
      fetchTOAdminPreferences,
      fetchTransportOrganization,
      changeDriverStatus,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(UpdateDriver);
